/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-param-reassign */
import type { BoxProps } from '@hover/blueprint';
import { Box, IconButton, Image, motion } from '@hover/blueprint';
import { iconClose } from '@hover/icons';
import { createUseGesture, dragAction, pinchAction } from '@use-gesture/react';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';

const useGesture = createUseGesture([dragAction, pinchAction]);

const DOUBLE_CLICK_TIME_THRESHOLD = 250;

interface ZoomProps extends BoxProps {
  image: string;
  onCloseClick?: () => void;
}

const MotionIconButton = motion(IconButton);

const Zoom = ({ image, onCloseClick, ...props }: ZoomProps) => {
  useEffect(() => {
    const handler = (e: Event) => e.preventDefault();
    document.addEventListener('gesturestart', handler);
    document.addEventListener('gesturechange', handler);
    document.addEventListener('gestureend', handler);
    return () => {
      document.removeEventListener('gesturestart', handler);
      document.removeEventListener('gesturechange', handler);
      document.removeEventListener('gestureend', handler);
    };
  }, []);

  const [style, api] = useSpring(() => ({
    x: 0,
    y: 0,
    scale: 1,
    rotateZ: 0,
    touchAction: 'none',
  }));

  const ref = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const lastClickTime = useRef(0);

  const [zoomed, setZoomed] = useState(false);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  });

  // Once we get the comfy image size, we need to try something like:
  // const maxScale = imageRef.current
  //   ? Math.max(
  //       imageRef.current.naturalWidth / imageRef.current.clientWidth,
  //       imageRef.current.naturalHeight / imageRef.current.clientHeight,
  //     )
  //   : 3;
  // to set max scale based on imageRef element size
  // and screen size to zoom up to the real image size
  // but at this moment the images are smaller, and them are giving us
  // max scales between 1.80 and 2, so we are using a fixed value of 3
  // losing a little bit of quality when zooming, but it's ok for now

  const maxScale = 3;

  useGesture(
    {
      onDrag: ({ pinching, cancel, offset: [x, y] }) => {
        // if not zoomed, lock image to starting position
        if (!zoomed) return api.start({ x: 0, y: 0 });

        // if pinching, cancel drag
        if (pinching) return cancel();

        // move image. Set inmediate: true to avoid delay
        api.start({ x, y, immediate: true });
        return null;
      },
      onPinch: ({
        origin: [ox, oy],
        first,
        movement: [ms],
        offset: [s],
        memo,
      }) => {
        if (first) {
          const { width, height, x, y } = ref.current!.getBoundingClientRect();
          const tx = ox - (x + width / 2);
          const ty = oy - (y + height / 2);
          memo = [style.x.get(), style.y.get(), tx, ty];
        }

        const x = memo[0] - (ms - 1) * memo[2];
        const y = memo[1] - (ms - 1) * memo[3];

        const width = imageRef.current ? imageRef.current.width * s : 300;
        const screenWidth = window.innerWidth;
        const height = imageRef.current ? imageRef.current.height * s : 300;

        setZoomed(s > 1);
        // if scale to the original size, reset image position
        api.start(zoomed ? { scale: s, rotateZ: 0, x, y } : { x: 0, y: 0 });

        // update new bounds
        setBounds({
          left: (-width + screenWidth) / 2,
          top: height > 600 ? -height / 2 : -300,
          right: (width - screenWidth) / 2,
          bottom: height > 600 ? height / 2 : 300,
        });

        return memo;
      },
      onClick: () => {
        const now = Date.now();
        if (now - lastClickTime.current < DOUBLE_CLICK_TIME_THRESHOLD) {
          api.start({ scale: zoomed ? 1 : maxScale, rotateZ: 0, x: 0, y: 0 });
          setZoomed(!zoomed);
        }
        lastClickTime.current = now;
      },
    },
    {
      target: ref,
      drag: {
        from: () => [style.x.get(), style.y.get()],
        bounds,
      },
      pinch: { scaleBounds: { min: 1, max: maxScale }, rubberband: true },
    },
  );

  const init = useSpring({
    from: { width: '80%' },
    to: { width: '100%' },
    config: { duration: 350 },
  });

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
      zIndex="modal"
      {...props}
    >
      <AnimatePresence initial={false}>
        {onCloseClick && (
          <MotionIconButton
            colorScheme="light"
            icon={iconClose}
            label="zoom"
            left={{ base: 400, tablet: 600 }}
            onClick={onCloseClick}
            position="absolute"
            shape="circle"
            top={{ base: 400, tablet: 600 }}
            transition={{ duration: 0.3 }}
            variant="glass"
            zIndex={2}
          />
        )}
      </AnimatePresence>
      <animated.div ref={ref} style={{ ...init, ...style }}>
        <Image
          alt="zoom"
          loading="eager"
          maxH="100%"
          maxW="100%"
          ref={imageRef}
          src={image}
        />
      </animated.div>
    </Box>
  );
};

export { Zoom };
